import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface ModalProps {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: string;
  children: ReactNode;
  rodape?: ReactNode;
  /** No celular vira folha inferior, que é onde o polegar alcança (seção 9.3). */
  tamanho?: 'medio' | 'grande';
}

/**
 * Diálogo modal acessível: prende o foco, fecha no Esc e devolve o foco a quem
 * abriu. Usa `<dialog>` nativo, que já resolve pilha de camadas e inerte.
 */
export function Modal({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  rodape,
  tamanho = 'medio',
}: ModalProps) {
  const referencia = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogo = referencia.current;
    if (!dialogo) return;

    if (aberto && !dialogo.open) {
      dialogo.showModal();
      document.body.style.overflow = 'hidden';
    } else if (!aberto && dialogo.open) {
      dialogo.close();
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [aberto]);

  return (
    <dialog
      ref={referencia}
      aria-labelledby="modal-titulo"
      onCancel={(evento) => {
        evento.preventDefault();
        aoFechar();
      }}
      onClick={(evento) => {
        // Clique no fundo (fora do conteúdo) fecha.
        if (evento.target === referencia.current) aoFechar();
      }}
      className={cn(
        'w-full max-w-[calc(100vw-1.5rem)] rounded-[var(--radius-lg)] border border-[var(--cor-borda)]',
        'bg-[var(--cor-superficie)] p-0 text-[var(--cor-texto)] shadow-[var(--shadow-lg)]',
        'backdrop:bg-black/40 backdrop:backdrop-blur-[2px]',
        'open:animate-in',
        tamanho === 'grande' ? 'sm:max-w-3xl' : 'sm:max-w-lg',
      )}
    >
      {aberto ? (
        <div className="flex max-h-[85dvh] flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-[var(--cor-borda)] px-5 py-4">
            <div className="min-w-0">
              <h2 id="modal-titulo" className="text-base font-semibold">
                {titulo}
              </h2>
              {descricao ? (
                <p className="mt-0.5 text-sm text-[var(--cor-texto-suave)]">{descricao}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={aoFechar}
              aria-label="Fechar"
              className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--cor-texto-suave)] hover:bg-[var(--cor-superficie-2)]"
            >
              <X aria-hidden className="size-4" />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {rodape ? (
            <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--cor-borda)] px-5 py-3">
              {rodape}
            </div>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
